import { useState } from 'react';
import { Table, Button, Form, Spinner, Alert } from 'react-bootstrap';
import { Trash2, Search } from 'lucide-react';
import type { RedirectRule } from '../../../../infrastructure/SiteConfigClient';

interface RedirectListProps {
  redirects: RedirectRule[];
  onDelete: (sourceUrl: string) => Promise<void>;
  isLoading?: boolean;
  isDeleting?: boolean;
}

export const RedirectList: React.FC<RedirectListProps> = ({
  redirects,
  onDelete,
  isLoading = false,
  isDeleting = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingSourceUrl, setDeletingSourceUrl] = useState<string | null>(null);

  const filteredRedirects = redirects.filter((redirect) =>
    redirect.sourceUrl.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleDelete = async (sourceUrl: string) => {
    if (window.confirm(`Are you sure you want to delete the redirect from "${sourceUrl}"?`)) {
      try {
        setDeletingSourceUrl(sourceUrl);
        await onDelete(sourceUrl);
      } catch (error) {
        alert(error instanceof Error ? error.message : 'Failed to delete redirect');
      } finally {
        setDeletingSourceUrl(null);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading redirects...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <Form.Group>
          <div className="position-relative">
            <Search className="position-absolute" style={{ left: '12px', top: '10px' }} size={18} />
            <Form.Control
              type="text"
              placeholder="Search by source URL..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ paddingLeft: '40px' }}
            />
          </div>
        </Form.Group>
      </div>

      {filteredRedirects.length === 0 ? (
        <Alert variant="info">
          {searchQuery ? 'No redirects found matching your search.' : 'No redirect rules configured.'}
        </Alert>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th style={{ width: '40%' }}>Source URL</th>
              <th style={{ width: '40%' }}>Target URL</th>
              <th style={{ width: '20%' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRedirects.map((redirect, index) => (
              <tr key={`${redirect.sourceUrl}-${index}`}>
                <td>
                  <code>{redirect.sourceUrl}</code>
                </td>
                <td>
                  <code>{redirect.targetUrl}</code>
                </td>
                <td>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(redirect.sourceUrl)}
                    disabled={isDeleting || deletingSourceUrl === redirect.sourceUrl}
                  >
                    {deletingSourceUrl === redirect.sourceUrl ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-1" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} className="me-1" />
                        Delete
                      </>
                    )}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {searchQuery && filteredRedirects.length > 0 && (
        <div className="text-muted mt-2">
          Showing {filteredRedirects.length} of {redirects.length} redirects
        </div>
      )}
    </div>
  );
};

